import { Button, IconButton } from 'component/Button'
import { Checkbox } from 'component/Checkbox'
import { useOptionalDropdown } from 'component/Dropdown/DropdownContext'
import { Input } from 'component/Input'
import { Option, toggleOption } from 'component/Select/Option'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { FilterStore } from 'page/catalog/store/FilterStore'
import { CSSProperties, Fragment, useState } from 'react'
import { ui } from 'store/home/ui/HomeUiStore'
import { getTranslation } from 'util/translations'
import { disk, emdash } from 'util/typo'
import css from './FilterPopover.module.scss'

type Props = {
  filter: FilterStore
  options: Option[]
  inline?: boolean
  search?: boolean
  style?: CSSProperties
}

export const FilterPopover = observer((props: Props) => {
  const { filter, options, inline, search: showSearch = true, style } = props
  const dropdown = useOptionalDropdown()
  const { small } = ui.window
  const { stand } = useDemo()
  const [search, setSearch] = useState('')
  const mass = showSearch && options.length > 8
  const { needTranslate, type } = filter

  const setClose = useLatestCallback(() => {
    if (!inline) dropdown?.setOpen(false)
  })

  const setValue = useLatestCallback((value: string[]) => {
    return (filter.value = value)
  })

  const onChangeItem = useLatestCallback((key: string) => {
    const next = toggleOption(key, filter.value, options)
    setValue(next)
  })

  const onReset = useLatestCallback(() => {
    setValue([])
    setClose()
  })

  const onClickSubmit = useLatestCallback(() => {
    filter.apply()
    setClose()
  })

  function match(option: Option) {
    const text = search.trim().toLowerCase()
    if (!text) return true

    const optValue = option.value.toLowerCase()
    const optLabel = (option.label || '').toLowerCase()

    const value = needTranslate
      ? getTranslation(type, optValue).toLowerCase()
      : optValue
    const label = needTranslate
      ? getTranslation(type, optLabel).toLowerCase()
      : optLabel

    return value.includes(text) || label.includes(text)
  }

  const filtered = options.filter(match)
  let current_group: string | undefined = ''
  let current_label: string | undefined = ''
  let used_values = new Set<string>

  return (
    <Vertical className={css.popover} style={style}>
      {small && !inline && (
        <Space
          className={css.header}
          gap={12}
          pad={[0, 0]}
          height={40}
          opposite
        >
          <Typo size={14} medium upper>
            {filter.title}
          </Typo>
          <IconButton small action={onClickSubmit}>
            <Icon name="close" size={24} />
          </IconButton>
        </Space>
      )}

      {mass && !stand && (
        <Space className={css.search} gap={12} pad={[12, 0]} opposite>
          <Input value={search} onChange={setSearch} />
        </Space>
      )}

      <Vertical className={css.scroll} gap={12} pad={[12, 0]}>
        {!filtered.length && (
          <Space height={20} center>
            <Typo size={12} secondary>
              {i18n('NothingFound')}
            </Typo>
          </Space>
        )}
        {filtered.map((option: Option) => {
          const { value, label, group } = option
          const checked = filter.value.includes(value)
          const title = group && group !== current_group
          const current = current_label === label && current_group === group
          const used = used_values.has(value)
          if (current || used) return null

          current_group = group
          current_label = label
          used_values.add(value)

          return (
            <Fragment key={value}>
              {title && (
                <Space gap={4}>
                  {group
                    .split('>')
                    .map((g) => g.trim())
                    .map((g, i) => (
                      <Fragment key={i}>
                        {i > 0 && (
                          <Typo size={11} accent tertiary>
                            {disk}
                          </Typo>
                        )}
                        <Typo size={15} accent secondary>
                          {g}
                        </Typo>
                      </Fragment>
                    ))}
                </Space>
              )}
              <Space gap={12}>
                <Checkbox
                  checked={checked}
                  onChange={() => onChangeItem(value)}
                >
                  {label || value || emdash}
                </Checkbox>
              </Space>
            </Fragment>
          )
        })}
      </Vertical>

      {!inline && (
        <Space className={css.footer} gap={12} pad={[12, 0]} spread wide wrap>
          <Button text action={onReset}>
            {i18n('demo.ResetAll')}
          </Button>
          <Button
            className={css.submit}
            invert
            action={() => {
              filter.apply()
              setClose()
            }}
          >
            {i18n('demo.Apply')}
          </Button>
        </Space>
      )}
    </Vertical>
  )
})
